import path from "path";
import { findPackageJson, safeReadJson } from "../utils/files.js";
import { isDependencyNative } from "../analyze/dependencyAnalyzer.js";
import { loadPluginsForProject } from "../plugins/loader.js";
import { getPackageKnowledge } from "../knowledge/knowledgeBase.js";
import type { ImpactReport, ImpactFinding } from "../models/ImpactReport.js";

/**
 * Analyzes the impact of installing a package into the current React Native project.
 *
 * Uses:
 *  - Filesystem detection (native module check)
 *  - Plugin system (compatibility rules, install hints, sync validations)
 *  - Knowledge base (known issues, documentation)
 */
export async function analyzeImpact(cwd: string, packageName: string): Promise<ImpactReport> {
  const packageJsonPath = findPackageJson(cwd);
  if (!packageJsonPath) throw new Error("package.json not found.");
  const pkg = safeReadJson(packageJsonPath);
  if (!pkg) throw new Error("package.json is corrupted.");

  const projectRoot = path.dirname(packageJsonPath);

  const deps = (pkg["dependencies"] as Record<string, string>) || {};
  const devDeps = (pkg["devDependencies"] as Record<string, string>) || {};
  const allDeps = { ...deps, ...devDeps };

  // Load plugins to see if the package has a plugin
  const loadedPlugins = await loadPluginsForProject(projectRoot);
  const plugin = loadedPlugins.find(
    (p) => p.packageName === packageName || packageName.startsWith(p.packageName + "/")
  );

  // Detect native status
  const isNative = isDependencyNative(projectRoot, packageName);
  const hasPlugin = !!plugin;

  // Knowledge base hints
  const knowledge = getPackageKnowledge(packageName);

  const installationSteps: string[] = [];
  const nativeChanges: string[] = [];
  const pluginValidations: string[] = [];
  const buildImplications: string[] = [];
  const findings: ImpactFinding[] = [];

  // 1. Installation steps from plugin or knowledge base
  if (plugin?.installHints) {
    installationSteps.push(...plugin.installHints());
  } else if (knowledge?.installHints) {
    installationSteps.push(...knowledge.installHints);
  } else {
    installationSteps.push(`Install with your package manager: npm install ${packageName}`);
  }

  // 2. Native changes
  if (isNative) {
    nativeChanges.push("This is a native module — native code will be added to the project.");
    if (process.platform === "darwin") {
      nativeChanges.push("Run pod install after installation.");
    }
    nativeChanges.push("A clean build may be required after installation.");
    buildImplications.push("Requires native rebuild on both iOS and Android.");

    if (knowledge?.iosRequirements?.length) {
      nativeChanges.push(...knowledge.iosRequirements.map((r) => `iOS: ${r}`));
    }
    if (knowledge?.androidRequirements?.length) {
      nativeChanges.push(...knowledge.androidRequirements.map((r) => `Android: ${r}`));
    }
  }

  // 3. Plugin validations
  if (plugin?.syncValidations) {
    const validations = await plugin.syncValidations(projectRoot);
    pluginValidations.push(...validations);
  }
  if (plugin?.compatibilityRules) {
    const rules = plugin.compatibilityRules();
    for (const rule of rules) {
      try {
        const triggered = await rule.check(projectRoot, allDeps);
        if (triggered) {
          findings.push({
            severity: "warning",
            category: "compatibility",
            message: rule.text || `Compatibility concern: ${rule.id}`,
            action: "Review plugin documentation for setup requirements.",
          });
        }
      } catch {
        // ignore rule errors
      }
    }
  }

  // 4. Knowledge-base common issues
  if (knowledge?.commonIssues?.length) {
    for (const issue of knowledge.commonIssues) {
      findings.push({
        severity: "info",
        category: "known-issues",
        message: issue,
      });
    }
  }

  if (knowledge?.compatibilityNotes?.length) {
    for (const note of knowledge.compatibilityNotes) {
      findings.push({
        severity: "info",
        category: "compatibility-notes",
        message: note,
      });
    }
  }

  // 5. Already installed?
  if (allDeps[packageName]) {
    findings.push({
      severity: "info",
      category: "status",
      message: `${packageName} is already present in your project (${allDeps[packageName]}).`,
      action: "No installation required — running impact analysis on existing version.",
    });
  }

  // Compute overall risk
  const hasErrors = findings.some((f) => f.severity === "error");
  const hasWarnings = findings.some((f) => f.severity === "warning");
  const overallRisk: ImpactReport["overallRisk"] = hasErrors
    ? "HIGH"
    : hasWarnings || isNative
    ? "MEDIUM"
    : "LOW";

  // Sort findings: error → warning → info
  const severityOrder = { error: 0, warning: 1, info: 2 };
  findings.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  return {
    packageName,
    isNative,
    hasPlugin,
    installationSteps,
    nativeChanges,
    pluginValidations,
    buildImplications,
    compatibilityFindings: findings,
    overallRisk,
    generatedAt: new Date().toISOString(),
  };
}
